/**
 * HTTP Auth Adapter — V2 Online Booking Engine
 *
 * All endpoints are under /api/v2.0/online-booking/auth/
 *
 * Public endpoints:  account, validate-mobile, signup, signin, send-otp, verify-otp,
 *                    send-email-code, verify-email-code, forgot-password
 * Protected endpoints: signout, me (require JWT)
 */

import { publicClient, authClient } from '@/services/httpClient'
import { API_VERSIONS, DEVICE_VALUE, resolveApiPath } from '@/config/api'

const AUTH_PREFIX = resolveApiPath(`${API_VERSIONS.AUTH}/auth`)

const resolveUrl = (path) => `${AUTH_PREFIX}${path}`

// ---------------------------------------------------------------------------
// Public endpoints
// ---------------------------------------------------------------------------

/**
 * Check if a mobile number already has an account.
 * POST /auth/validate-mobile
 */
export const validateMobile = async ({ mobile, source, fullName, email, password, passwordConfirmation }) => {
  const body = { mobile, device: DEVICE_VALUE }

  if (source === 'SIGNUP') {
    body.source = 'SIGNUP'
    if (fullName) body.full_name = fullName
    if (email) body.email = email
    if (password) {
      body.password = password
      body.password_confirmation = passwordConfirmation || password
    }
  }

  const response = await publicClient.post(resolveUrl('/validate-mobile'), body)
  return response?.data
}

/**
 * Sign in with mobile + password.
 * POST /auth/signin
 */
export const login = async ({ mobile, password }) => {
  const response = await publicClient.post(resolveUrl('/signin'), {
    mobile,
    password,
    device: DEVICE_VALUE
  })
  return response?.data
}

/**
 * Create a new guest account. Returns JWT token directly.
 * POST /auth/signup
 *
 * NOTE: the backend requires the email to be verified first (via
 * send-email-code / verify-email-code) — otherwise signup is rejected.
 *
 * `verify_id` is the id send-otp returned for the mobile OTP this sign-up
 * just passed. The API needs it as proof when the mobile already exists
 * there — which it always does by now, because verify-otp creates the guest
 * — and answers 422 `OTP_REQUIRED` without it. Sent only when there is one.
 */
export const signup = async ({ mobile, fullName, email, password, passwordConfirmation, callingCode, verifyId }) => {
  const response = await publicClient.post(resolveUrl('/signup'), {
    full_name: fullName,
    callingcode: callingCode || '+91',
    mobile,
    email: email || '',
    password,
    password_confirmation: passwordConfirmation,
    device: DEVICE_VALUE,
    ...(verifyId ? { verify_id: verifyId } : {})
  })
  return response?.data
}

/**
 * Send OTP to a mobile number.
 * POST /auth/send-otp
 */
export const sendOtp = async ({ mobile }) => {
  const response = await publicClient.post(resolveUrl('/send-otp'), {
    mobile,
    device: DEVICE_VALUE
  })
  return response?.data
}

/**
 * Verify OTP — returns JWT token (auto-creates user if needed).
 * POST /auth/verify-otp
 */
export const verifyOtp = async ({ mobile, otp, verifyId }) => {
  const response = await publicClient.post(resolveUrl('/verify-otp'), {
    mobile,
    otp,
    otpPayload: {
      verify_id: verifyId
    },
    device: DEVICE_VALUE
  })
  return response?.data
}

/**
 * Send a 6-digit verification code to an email (signup flow).
 * POST /auth/send-email-code
 */
export const sendEmailOtp = async ({ email, fullName }) => {
  const body = { email, device: DEVICE_VALUE }
  if (fullName) body.full_name = fullName
  const response = await publicClient.post(resolveUrl('/send-email-code'), body)
  return response?.data
}

/**
 * Verify the 6-digit email code.
 * POST /auth/verify-email-code
 */
export const verifyEmailOtp = async ({ email, code }) => {
  const response = await publicClient.post(resolveUrl('/verify-email-code'), {
    email,
    code
  })
  return response?.data
}

/**
 * Reset password using mobile + OTP.
 * Requires a prior send-otp call.
 * POST /auth/forgot-password
 */
export const forgotPassword = async ({ mobile, otp, verifyId, password, passwordConfirmation }) => {
  const response = await publicClient.post(resolveUrl('/forgot-password'), {
    mobile,
    otp,
    otpPayload: {
      verify_id: verifyId
    },
    password,
    password_confirmation: passwordConfirmation,
    device: DEVICE_VALUE
  })
  return response?.data
}

// ---------------------------------------------------------------------------
// Protected endpoints (JWT required)
// ---------------------------------------------------------------------------

/**
 * Invalidate the current JWT token.
 * POST /auth/signout
 */
export const signout = async () => {
  const response = await authClient.post(resolveUrl('/signout'))
  return response?.data
}

/**
 * Get the currently authenticated user's profile.
 * GET /auth/me
 */
export const fetchMe = async () => {
  const response = await authClient.get(resolveUrl('/me'))
  return response?.data
}

/**
 * Validate a JWT token from another app (cross-app SSO).
 * POST /auth/validate-token
 *
 * Used when user navigates from management app with ?sso_token=xxx
 * Returns a fresh OBE token if the incoming token is valid.
 */
export const validateToken = async ({ token, source = 'MANAGEMENT' }) => {
  const response = await publicClient.post(resolveUrl('/validate-token'), {
    token,
    source
  })
  return response?.data
}
